import React from "react";
import { Box, Card, CardContent, Tab, Tabs, Typography } from "@mui/material";
import ExtruderSettingsSection from "./ExtruderSettingsSection";
import { useAuth } from "../../../contexts/AuthContext";
import { useStickyTab } from "../../../utils/uiPrefs";
import ShiftSettingsSection from "./ShiftSettingsSection";
import BrandsSection from "./BrandsSection";
import UserManagementSection from "./UserManagementSection";
import FoamSettingsSection from "./FoamSettingsSection";

const SettingsPage: React.FC = () => {
  const { token, hasAdminAccess, canManageUsers, role } = useAuth();

  const api = process.env.REACT_APP_API_URL || "";

  // Built from what this role may see, so the tab strip never offers a section
  // that would render nothing. Managers get everything except the users table;
  // operators get the shared plant settings, so they can see what the diagrams
  // and dashboards are judging against.
  const tabs: { id: string; label: string; render: () => React.ReactNode }[] = [
    ...(canManageUsers
      ? [
          {
            id: "users",
            label: "Users",
            render: () => (
              <UserManagementSection api={api} token={token ?? undefined} />
            ),
          },
        ]
      : []),
    ...(hasAdminAccess
      ? [
          {
            id: "brands",
            label: "Brands",
            render: () => <BrandsSection api={api} token={token ?? undefined} />,
          },
        ]
      : []),
    {
      id: "extruders",
      label: "Extruders",
      render: () => (
        <ExtruderSettingsSection api={api} token={token ?? undefined} />
      ),
    },
    {
      id: "shifts",
      label: "Shifts",
      render: () => (
        <ShiftSettingsSection api={api} token={token ?? undefined} />
      ),
    },
    {
      id: "foam",
      label: "Foam",
      // Saving needs admin or manager; the section is visible to all so
      // operators can see what the diagram is judging against.
      render: () => <FoamSettingsSection />,
    },
  ];

  // Keyed by role: the visible set differs between roles, so a stored index
  // would otherwise restore a different section for whoever logs in next.
  const [tab, setTab] = useStickyTab(`settings.${role ?? "anon"}`, tabs.length);
  const active = tabs[tab] ?? tabs[0];

  return (
    <Box sx={{ p: { xs: 0, sm: 1 }, maxWidth: 1100, mx: "auto" }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: { xs: 1.5, sm: 2 } }}>
        Settings
      </Typography>

      <Card sx={{ mb: { xs: 1.5, sm: 2 } }}>
        <CardContent sx={{ p: { xs: 0.5, sm: 1 }, "&:last-child": { pb: { xs: 0.5, sm: 1 } } }}>
          <Tabs
            value={Math.min(tab, tabs.length - 1)}
            onChange={(_, v) => setTab(v)}
            aria-label="Settings sections"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {tabs.map((t) => (
              <Tab
                key={t.id}
                label={t.label}
                id={`settings-tab-${t.id}`}
                aria-controls={`settings-panel-${t.id}`}
                sx={{ minWidth: "auto", px: { xs: 1.5, md: 2.5 } }}
              />
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Only the active section is mounted: several of these fetch on mount,
          and the old stacked page fired every one of those on every visit. */}
      <Box
        role="tabpanel"
        id={`settings-panel-${active.id}`}
        aria-labelledby={`settings-tab-${active.id}`}
      >
        {active.render()}
      </Box>
    </Box>
  );
};

export default SettingsPage;
